import { computed, ref } from '@nuxtjs/composition-api';

import { Logger } from '~/helpers/logger';

export function useTruncatedText(props, $markdown) {
  const isTruncated = ref(props.truncate !== 'none');

  const processText = (text = '', isMarkdown = false, isAnimatedText = false) => {
    let result = text;

    try {
      if (isMarkdown) {
        result = $markdown.render(result);
      }
      if (isAnimatedText) {
        result = result
          .split(/(<[^>]+>|\s+)/) // Split into HTML tags, spaces, or words
          .filter((part) => part.trim() !== '' || part === ' ') // Keep spaces intact
          .map((part) => {
            if (part.startsWith('<') && part.endsWith('>')) {
              return part; // HTML tags remain unchanged
            }
            if (part.trim() === '') {
              return part; // Spaces remain unchanged
            }
            // aria-hidden="true" to prevent anounce each part by screen reader
            return `<span class="animate-description" aria-hidden="true">${part}</span>`;
          })
          .join('');
      }
    } catch (error) {
      Logger.error('VaimoTruncatedText: Error processing text', error);
    }

    return result;
  };

  const truncatedText = computed(() => {
    const { truncate, characters, teaser, text } = props;
    if (!text) return null;

    if (truncate === 'by characters' || truncate === 'by characters then lines') {
      if (!characters || text.length <= characters) return null;

      let trimmedString = text.substring(0, characters);
      const lastSpaceIndex = trimmedString.lastIndexOf(' ');
      if (lastSpaceIndex > 0) {
        trimmedString = trimmedString.substring(0, lastSpaceIndex);
      }

      return trimmedString + '...';
    }

    if (truncate === 'by lines' || truncate === 'by double text') {
      return text;
    }

    if (truncate === 'by teaser') {
      return teaser ? `${teaser}...` : null;
    }

    return null;
  });

  const showLineClamp = computed(() => {
    if (!props.lines) return false;

    const truncatedByLine = props.truncate === 'by lines';
    const truncatedByCharactersAndLines = props.truncate === 'by characters then lines';
    const hasTruncatedText = Boolean(truncatedText.value);

    return truncatedByLine || (truncatedByCharactersAndLines && hasTruncatedText);
  });

  const renderedText = computed(() => {
    const baseText = isTruncated.value ? truncatedText.value || props.text : props.text;

    return processText(baseText, props.markdown, props.animatedText);
  });

  const renderedTextLong = computed(() => processText(props.textLong || '', props.markdown, props.animatedText));

  const textClasses = computed(() => {
    const list = [`vaimo-truncated-text--truncated-${props.truncate.replace(/\s+/g, '-')}`];

    if (isTruncated.value) list.push('is-truncated');
    if (showLineClamp.value && props.lines) {
      list.push(`line-truncate-${props.lines}`);
    }
    if (props.textClass) list.push(props.textClass);

    return list;
  });

  const toggle = () => {
    isTruncated.value = !isTruncated.value;
  };

  return {
    isTruncated,
    truncatedText,
    renderedText,
    renderedTextLong,
    textClasses,
    showLineClamp,
    toggle
  };
}
